import { useEffect, useState } from "react";
import { AlertTriangle, Inbox, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDateTime } from "@/lib/dates";
import { cn } from "@/lib/utils";

export const RAG_META = {
  red: { label: "Critical", dot: "bg-[hsl(var(--rag-red))]", text: "text-[hsl(var(--rag-red))]", ring: "ring-[hsl(var(--rag-red))]/30", bg: "bg-[hsl(var(--rag-red))]/10" },
  amber: { label: "At risk", dot: "bg-[hsl(var(--rag-amber))]", text: "text-[hsl(var(--rag-amber))]", ring: "ring-[hsl(var(--rag-amber))]/30", bg: "bg-[hsl(var(--rag-amber))]/10" },
  green: { label: "Healthy", dot: "bg-[hsl(var(--rag-green))]", text: "text-[hsl(var(--rag-green))]", ring: "ring-[hsl(var(--rag-green))]/30", bg: "bg-[hsl(var(--rag-green))]/10" },
};

export const RagDot = ({ status, showLabel = true, testId }) => {
  const m = RAG_META[status] || RAG_META.green;
  return (
    <span className="inline-flex items-center gap-2" data-testid={testId}>
      <span className={cn("h-2.5 w-2.5 rounded-full ring-4", m.dot, m.ring)} />
      {showLabel && <span className={cn("text-xs font-medium", m.text)}>{m.label}</span>}
    </span>
  );
};

/** `compact` is for a long strip that has to stay on one line — smaller box, smaller type, and no
 *  `flex-1 min-w-` so the grid around it owns the width. */
export const StatTile = ({ label, value, sub, accent, testId, icon: Icon, compact }) => (
  <div
    data-testid={testId}
    className={cn("group relative border bg-card transition-shadow duration-200 hover:shadow-md",
      compact ? "rounded-lg p-2.5" : "flex-1 min-w-[140px] rounded-xl p-4")}
  >
    <div className="flex items-start justify-between">
      <p className={cn("font-medium uppercase tracking-wider text-muted-foreground",
        compact ? "text-[9px] leading-tight" : "text-[11px]")}>{label}</p>
      {Icon && <Icon className={compact ? "h-3.5 w-3.5 text-muted-foreground/60" : "h-4 w-4 text-muted-foreground/60"} />}
    </div>
    <p className={cn("num font-semibold tracking-tight [overflow-wrap:anywhere]",
      compact ? "mt-1 text-base sm:text-lg" : "mt-2 text-xl sm:text-2xl", accent)}>{value}</p>
    {sub && <p className={cn("text-muted-foreground",
      compact ? "mt-0.5 text-[10px] leading-snug" : "mt-0.5 text-xs")}>{sub}</p>}
  </div>
);

export const EmptyState = ({ icon: Icon = Inbox, title, description, action, testId }) => (
  <div
    data-testid={testId}
    className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-card/50 px-6 py-12 text-center"
  >
    <div className="rounded-full bg-accent p-3">
      <Icon className="h-6 w-6 text-brand-yellow-ink" />
    </div>
    <div>
      <p className="font-display text-base font-semibold">{title}</p>
      {description && <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>}
    </div>
    {action}
  </div>
);

export const TableSkeleton = ({ rows = 8, cols = 6 }) => (
  <div className="space-y-2" data-testid="table-skeleton">
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="flex items-center gap-4 rounded-lg border bg-card px-4 py-3">
        {Array.from({ length: cols }).map((__, c) => (
          <Skeleton key={c} className="h-4" style={{ width: c === 0 ? "22%" : `${60 / cols}%` }} />
        ))}
      </div>
    ))}
  </div>
);

export const Spinner = ({ className }) => (
  <Loader2 className={cn("h-4 w-4 animate-spin", className)} />
);

export const ErrorNote = ({ message, testId }) => (
  <div
    data-testid={testId || "error-note"}
    className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm"
  >
    <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
    <span>{message}</span>
  </div>
);

export const relativeTime = (iso) => {  if (!iso) return "never";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (Number.isNaN(diff)) return "unknown";
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const absoluteTime = (iso) => fmtDateTime(iso);

export const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};
